import { ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import Header from './Header'
import Footer from './Footer'
import { useAuthStore } from '../stores/authStore'
import { getAuthToken } from '../utils/authStorage'

import { LEGAL_PATHS, MARKETING_SHELL_PATHS } from '../content/legal'
import { isStudentWorkspacePath } from '../utils/studentWorkspacePath'
import { pathProvidesOwnChrome } from '../utils/layoutChrome'

interface LayoutProps {
  children: ReactNode
}

const LOGIN_PATH_PREFIXES = ['/school', '/tutor', '/admin']

function isLoginPath(pathname: string): boolean {
  if (pathname === '/') return false
  return LOGIN_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  )
}

const Layout = ({ children }: LayoutProps) => {
  const location = useLocation()
  const isHomePage = location.pathname === '/' || location.pathname === '/home'
  const isLegalPage = LEGAL_PATHS.includes(location.pathname as (typeof LEGAL_PATHS)[number])
  const isMarketingShell = MARKETING_SHELL_PATHS.some(
    (path) => location.pathname === path || location.pathname.startsWith(`${path}/`),
  )
  const isLoginPage = isLoginPath(location.pathname)
  const { isLoggedIn, user, _hasHydrated } = useAuthStore()

  const isLogoutTransition =
    typeof document !== 'undefined' &&
    document.getElementById('logout-loading-overlay') != null

  const isDashboardRoute = !isHomePage && !isLoginPage
  const hasSessionToken = Boolean(getAuthToken())
  const hideHeaderDuringStudentHydration =
    !_hasHydrated && hasSessionToken && isStudentWorkspacePath(location.pathname)
  const hideHeaderForStudent =
    (isLoggedIn && user?.role === 'student' && isDashboardRoute) ||
    hideHeaderDuringStudentHydration
  const isAffiliateSupportShell =
    location.pathname === '/support' &&
    ((_hasHydrated && isLoggedIn && user?.role === 'affiliate') ||
      (!_hasHydrated && hasSessionToken &&
        typeof window !== 'undefined' &&
        window.localStorage.getItem('userRole') === 'affiliate'))

  // Always hide on `/` — Home owns MarketingHeader (avoids logged-in FOUC of legacy Header).
  const hideHeaderOnLanding =
    isHomePage ||
    isLegalPage ||
    isMarketingShell ||
    isAffiliateSupportShell

  // DashboardLayout / portal login / auth callbacks already render their own chrome.
  const hideHeaderForOwnedChrome = pathProvidesOwnChrome(location.pathname)

  const showFooter = false

  if (isLogoutTransition) {
    return (
      <div className="layout-container layout-container--auth-transition">
        {children}
      </div>
    )
  }

  return (
    <div className={`layout-container ${isHomePage ? 'homepage' : ''}`}>
      {!hideHeaderForStudent &&
        !hideHeaderOnLanding &&
        !hideHeaderForOwnedChrome && <Header />}
      {children}
      {showFooter && <Footer />}
    </div>
  )
}

export default Layout
